import React, { useState, useMemo, useCallback, useEffect } from "react";
import { Head } from "@inertiajs/react";
import { CalendarIcon, UserIcon, EyeIcon, ShareIcon } from "lucide-react";
import HomeHeader from "@/components/layouts/headers/HomeHeader";
import {
  LazyNewsReactionBar,
  LazyCommentSection,
  LazySelebrityRankWidget,
  ProgressiveLoader,
} from "@/components/lazy/LazyComponents";
import parse from "html-react-parser";
import DOMPurify from "dompurify";
import LazyImage from "@/components/ui/LazyImage";
import AdsBlock from "@/components/ads/AdsBlock";
import HomeFooter from "@/components/layouts/footers/HomeFooter";
import { getImageUrl } from "@/utils/unifiedImageUtils";
import { generateLQIPUrl } from "@/utils/lqipUtils";
import DynamicNewsSection from "@/components/sections/DynamicNewsSection";

import { useLatestNews } from "@/hooks/useNews";
import { ScrollToTop } from "@/components/ui/ScrollToTop";
import { NewsErrorBoundary } from "@/components/ui/ErrorBoundary";
import CarouselErrorBoundary from "@/components/ui/CarouselErrorBoundary";

interface User {
  id: number;
  name: string;
  email: string;
  username?: string;
}

interface News {
  id: number;
  title: string;
  slug: string;
  content: string;
  image?: string;
  image_url?: string;
  image_alt?: string;
  views: number;
  created_at: string;
  updated_at: string;
  author: User;
  category: {
    id: number;
    name: string;
    slug: string;
  };
  reactions: {
    counts: {
      like: number;
    };
    total_count: number;
    user_reaction: string | null;
  };
  comments_count: number;
}

interface NewsDetailProps {
  user?: User;
  news: News;
  relatedNews?: News[];
  comments?: any[];
  deviceId?: string;
  reactionTypes?: Record<string, string>;
  pageTitle?: string;
  categories?: Array<{
    id: number;
    name: string;
    slug: string;
  }>;
  celebrityRanks?: Array<{
    id: number;
    name: string;
    photo: string;
    rank: number;
  }>;
}

const NewsDetail: React.FC<NewsDetailProps> = ({
  user,
  news,
  relatedNews = [],
  comments = [],
  deviceId: _deviceId,
  reactionTypes: _reactionTypes = {},
  pageTitle = "Detail Berita",
  categories = [],
  celebrityRanks = [],
}) => {
  const [commentsLoaded, setCommentsLoaded] = useState(false);

  // Data fetching is now handled by DynamicNewsSection components
  // which use optimized hooks internally with proper caching and error handling

  // Get latest news for prefetching links
  const { data: _latestNewsData = [] } = useLatestNews({
    enabled: true,
  });

  // Note: Prefetching is now handled by DynamicNewsSection internally
  // through lazy loading and viewport-based prefetching

  // Debug: Log the actual data structure received (temporary)
  console.log("NewsDetail - Full props:", { news, relatedNews: undefined, comments: undefined, user });
  console.log("NewsDetail - News structure:", {
    id: news?.id,
    title: news?.title,
    content: news?.content,
    image: news?.image,
    image_url: news?.image_url,
    image_alt: news?.image_alt,
    author: news?.author,
    category: news?.category,
    allKeys: Object.keys(news || {})
  });

  // Memoized news data to prevent unnecessary re-renders
  const memoizedNews = useMemo(() => news, [news.id, news.title, news.content]);
  const memoizedCategories = useMemo(() => categories, [categories]);

  // Safety checks for required data
  if (!news) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-gray-900 mb-4">
            Artikel tidak ditemukan
          </h1>
          <p className="text-gray-600">
            Artikel yang Anda cari tidak tersedia.
          </p>
        </div>
      </div>
    );
  }

  // Using centralized image utilities via OptimizedImage component

  // Memoized safe data to prevent unnecessary re-calculations
  const safeReactions = useMemo(() => {
    const reactions = news.reactions || {
      counts: { like: 0 },
      total_count: 0,
      user_reaction: null,
    };

    return reactions;
  }, [news.reactions]);

  const safeCategory = useMemo(
    () =>
      news.category || {
        id: 0,
        name: "Uncategorized",
        slug: "uncategorized",
      },
    [news.category],
  );

  const safeAuthor = useMemo(
    () =>
      news.author || {
        id: 0,
        name: "Unknown",
        email: "",
        username: "Anonim",
      },
    [news.author],
  );

  const handleShare = useCallback(async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: memoizedNews.title,
          text: `Baca berita: ${memoizedNews.title}`,
          url: window.location.href,
        });
      } catch (_error) {}
    } else {
      // Fallback: copy to clipboard
      try {
        await navigator.clipboard.writeText(window.location.href);
        alert("Link berhasil disalin ke clipboard!");
      } catch (_error) {}
    }
  }, [memoizedNews.title]);

  const formatDate = useCallback((dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString("id-ID", {
      year: "numeric",
      month: "long",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  }, []);

  const formatContent = useCallback((content: string) => {
    if (!content) return "";

    // Sanitize HTML content
    const sanitizedHtml = DOMPurify.sanitize(content, {
      ALLOWED_TAGS: [
        "p",
        "br",
        "strong",
        "em",
        "u",
        "h1",
        "h2",
        "h3",
        "h4",
        "h5",
        "h6",
        "ul",
        "ol",
        "li",
        "blockquote",
        "a",
        "img",
        "table",
        "thead",
        "tbody",
        "tr",
        "th",
        "td",
        "code",
        "pre",
        "span",
        "div",
      ],
      ALLOWED_ATTR: [
        "href",
        "src",
        "alt",
        "title",
        "class",
        "id",
        "target",
        "rel",
      ],
    });

    // Parse the sanitized HTML and return as React elements
    return parse(sanitizedHtml);
  }, []);

  // Intersection Observer for lazy loading comments
  useEffect(() => {
    const observerOptions = {
      root: null,
      rootMargin: "100px",
      threshold: 0.1,
    };

    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (
          entry.isIntersecting &&
          entry.target.id === "comments-section" &&
          !commentsLoaded
        ) {
          setCommentsLoaded(true);
        }
      });
    }, observerOptions);

    const commentsSection = document.getElementById("comments-section");
    if (commentsSection) observer.observe(commentsSection);

    return () => observer.disconnect();
  }, [commentsLoaded]);

  return (
    <>
      <Head title={`${memoizedNews.title} - ${pageTitle}`}>
        <meta name="description" content={memoizedNews.title} />
        <meta name="author" content="Lugu Software" />
        <meta name="robots" content="index, follow" />
        <meta name="googlebot" content="index, follow" />
        <meta name="google" content="notranslate" />
        <meta name="language" content="Indonesia" />
        <meta name="geo.region" content="ID" />
        <meta name="geo.placename" content="Indonesia" />
        <meta name="geo.position" content="-6.200000;106.816666" />
      </Head>

      <HomeHeader user={user} categories={memoizedCategories} />

      <p hidden>
        © Develop by{" "}
        <a href="http://software.lugu.id" target="_blank" rel="referrer">
          <strong className="text-lg">Lugu Software</strong>
        </a>{" "}
        All rights reserved.{" "}
      </p>

      <main className="px-4 py-6 bg-lg-background dark:bg-dr-background">
        <section className="container mx-auto">
          {/* Article Header */}
          <div className="w-full flex flex-col gap-6 pt-16 mb-8">
            <span className="bg-[#F82B44] text-white text-xs lg:text-base font-bold lg:font-extrabold rounded-sm w-fit px-8 py-2 -mb-4">
              {safeCategory.name}
            </span>
            <h1 className="text-2xl md:text-4xl lg:text-[4rem] font-bold text-stone-700 dark:text-stone-100 leading-relaxed lg:leading-snug">
              {memoizedNews.title}
            </h1>
          </div>

          {/* Article Image */}
          <div className="relative mb-8">
            <LazyImage
              src={getImageUrl(memoizedNews)}
              alt={memoizedNews.image_alt || memoizedNews.title}
              className="w-full aspect-video object-cover transition-all duration-500 hover:scale-105 rounded-lg"
              priority={true}
              loading="eager"
              lowQualitySrc={
                generateLQIPUrl(getImageUrl(memoizedNews), {
                  width: 20,
                  height: 15,
                  quality: 30,
                }).url
              }
              aspectRatio="16/9"
              maxRetries={3}
              retryBaseDelayMs={1000}
              debug={false}
            />

            {/* Image Alt Text */}
            {(memoizedNews.image_alt || memoizedNews.image) && (
              <div className="mt-3 text-center">
                <p className="text-sm text-gray-500 dark:text-gray-400 italic">
                  {memoizedNews.image_alt || "Gambar artikel"}
                </p>
              </div>
            )}
          </div>

          {/* Main Content Layout */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Main Content */}
            <div className="lg:col-span-2">
              <article className="bg-lg-background dark:bg-dr-background dark:text-white">
                {/* Article Content */}

                <div className="flex items-center space-x-4 text-sm text-gray-600 dark:text-gray-400 mb-2">
                  <div className="flex items-center space-x-1">
                    <UserIcon size={16} />
                    <span>Oleh</span>
                    <span className="font-bold">
                      {safeAuthor.username || "Anonim"}
                    </span>
                  </div>
                  <div className="flex items-center space-x-1">
                    <CalendarIcon size={16} />
                    <span>
                      {formatDate(
                        memoizedNews.created_at || new Date().toISOString(),
                      )}
                    </span>
                  </div>
                </div>
                <div
                  className="prose prose-lg lg:prose-xl max-w-none prose-slate dark:prose-invert
                  prose-headings:font-semibold prose-headings:text-balance
                  prose-p:leading-7 prose-p:my-4
                  prose-a:underline underline-offset-2 hover:prose-a:opacity-90
                  prose-strong:font-semibold
                  prose-img:rounded-xl prose-img:shadow-md prose-img:mx-auto prose-img:max-w-full prose-img:h-auto
                  prose-blockquote:border-l-4 prose-blockquote:border-blue-500
                  prose-blockquote:bg-gray-50 dark:prose-blockquote:bg-neutral-900
                  prose-blockquote:pl-4 prose-blockquote:py-2 prose-blockquote:italic
                  prose-ul:space-y-2 prose-ol:space-y-2
                  prose-li:marker:text-current
                  prose-code:bg-zinc-100 dark:prose-code:bg-zinc-800
                  prose-code:px-1.5 prose-code:py-0.5 prose-code:rounded-md
                  prose-pre:bg-zinc-900 prose-pre:text-zinc-100
                  prose-table:border-collapse prose-table:w-full
                  prose-th:border prose-th:border-zinc-300 prose-th:bg-zinc-50 dark:prose-th:bg-neutral-900
                  prose-td:border prose-td:border-zinc-300 prose-td:p-2
                  first:prose-p:mt-0 last:prose-p:mb-0
                  tinymce-content text-justify
"
                >
                  {formatContent(memoizedNews.content)}
                </div>

                {/* Article Metadata */}
                <div className="flex flex-wrap items-center gap-4 mt-4 mb-16">
                  <div className="flex items-center gap-4">
                    <LazyNewsReactionBar
                      reactableType="news"
                      reactableId={memoizedNews.id}
                      initialCounts={{
                        like: safeReactions.counts?.like || 0,
                      }}
                      initialUserReaction={safeReactions.user_reaction}
                      isAuthenticated={!!user}
                    />
                    <button
                      onClick={handleShare}
                      className="hover:scale-105 cursor-pointer flex items-center gap-2 text-gray-700 dark:text-white hover:text-blue-500 dark:hover:text-blue-500 transition-all"
                    >
                      <ShareIcon size={16} />
                      <span className="text-sm">Bagikan</span>
                    </button>
                  </div>
                  <div className="flex items-center space-x-1">
                    {/* <EyeIcon size={16} />
                    <span>
                      {(memoizedNews.views || 0).toLocaleString()} views
                    </span> */}
                  </div>
                </div>
              </article>

              {/* Horizontal Ads Section */}
              <section className="my-8">
                {(() => {
                  const detailBanner = [
                    {
                      id: "detail-hero-1",
                      filename: "h_banner_entertainment.jpg",
                      href: "https://www.instagram.com/lugu.entertainment/?hl=en",
                      orientation: "horizontal" as const,
                      aspectRatio: "6/1",
                      alt: "Entertainment Banner",
                    },
                  ];
                  return <AdsBlock items={detailBanner} />;
                })()}
              </section>

              {/* Related News Section - Shadcn Carousel */}
              <section className="my-16">
                <CarouselErrorBoundary
                  title="Berita Terkait"
                  onError={(_error, errorInfo) => {
                    console.error(
                      "Related News Carousel Error:",
                      _error,
                      errorInfo,
                    );
                  }}
                >
                  <DynamicNewsSection
                    title="Berita Terkait"
                    type="latest"
                    categorySlug={
                      safeCategory.slug !== "uncategorized"
                        ? safeCategory.slug
                        : undefined
                    }
                    layout="carousel"
                    cardVariant="compact"
                    maxItems={8}
                    showViewAll={false}
                    showLoadMoreButton={false}
                    showEndIndicator={false}
                    pageSize={8}
                    className=""
                    badge={{
                      text: "Terbaru",
                      color: "bg-[#de0b66]",
                    }}
                    icon="📰"
                    carouselOptions={{
                      itemsPerView: 3,
                      autoPlay: true,
                      autoPlayDelay: 6000,
                      showArrows: true,
                      showDots: false,
                      infinite: true,
                    }}
                  />
                </CarouselErrorBoundary>
              </section>

              {/* Comments Section - Lazy Loaded */}
              <div
                id="comments-section"
                className="mt-8 bg-lg-background dark:bg-dr-background dark:text-white"
              >
                {commentsLoaded ? (
                  <LazyCommentSection
                    newsSlug={memoizedNews.slug}
                    user={user}
                    isAuthenticated={!!user}
                    commentsCount={memoizedNews.comments_count || 0}
                  />
                ) : (
                  <div className="p-8 text-center">
                    <div className="animate-pulse">
                      <div className="h-6 bg-gray-200 dark:bg-gray-700 rounded w-1/4 mx-auto mb-4"></div>
                      <div className="space-y-3">
                        <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded"></div>
                        <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-5/6"></div>
                        <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-4/6"></div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Sidebar */}
            <div className="lg:col-span-1">
              <div className="sticky top-32 space-y-6">
                {/* Sidebar Related News */}
                {/*<div className="bg-lg-background dark:bg-dr-background dark:text-white rounded-lg overflow-hidden">
                  <DynamicNewsSection
                    title="Berita Lainnya"
                    type="latest"
                    variant="list"
                    maxItems={8}
                    showViewAll={true}
                    showLoadMoreButton={false}
                    showEndIndicator={false}
                    pageSize={8}
                    className="p-0"
                  />
                </div>*/}

                {/* Most Viewed Section */}
                <div className="bg-lg-background dark:bg-dr-background dark:text-white rounded-lg overflow-hidden">
                  <NewsErrorBoundary title="Most Viewed">
                    <DynamicNewsSection
                      title="Paling Dilihat"
                      type="mostsee"
                      sortBy="views"
                      variant="list"
                      maxItems={5}
                      showViewAll={false}
                      showLoadMoreButton={false}
                      showEndIndicator={false}
                      pageSize={5}
                      badge={{ text: "Trending", color: "bg-green-500" }}
                      className="pl-8"
                    />
                  </NewsErrorBoundary>
                </div>

                {/* Celebrity Rankings */}
                {celebrityRanks && celebrityRanks.length > 0 && (
                  <ProgressiveLoader threshold={0.2}>
                    <LazySelebrityRankWidget
                      celebrities={celebrityRanks}
                      title="LambeRank Selebriti"
                      maxItems={6}
                    />
                  </ProgressiveLoader>
                )}
              </div>
            </div>
          </div>
        </section>
      </main>

      <HomeFooter categories={categories} />
      <ScrollToTop show={true} />
    </>
  );
};

export default NewsDetail;
